import { useCallback, useState } from 'react';
import { CopyIcon, PinIcon } from '@/components/icons';
import { useWorkspace } from '@/context';
import { useSavedActions } from '@/hooks/useSavedActions';
import { renderWithMentions } from '@/utils/renderMentions';

import { CsvAttachmentChip } from '@/components/bulk-import/CsvAttachmentChip';

export interface UserMessageBubbleProps {
  content: string;
  attachment?: { kind: 'csv'; fileName: string };
  isPinned?: boolean;
  onPin?: (content: string) => void;
}

export function UserMessageBubble({
  content,
  attachment,
  isPinned: isPinnedProp,
  onPin: onPinProp,
}: UserMessageBubbleProps) {
  const [copied, setCopied] = useState(false);
  const { effectiveRole } = useWorkspace();
  const { pinTemplate, isTemplatePinned } = useSavedActions(effectiveRole);

  const onPin = onPinProp ?? pinTemplate;
  const isPinned = isPinnedProp ?? isTemplatePinned(content);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [content]);

  return (
    <div className="flow-thread__message-item">
      {attachment?.kind === 'csv' ? <CsvAttachmentChip fileName={attachment.fileName} /> : null}
      <div className="flow-thread__message">{renderWithMentions(content)}</div>
      <div className="flow-thread__message-actions">
        <button
          type="button"
          className={`flow-thread__message-action flow-thread__message-pin${isPinned ? ' flow-thread__message-pin--filled' : ''}`}
          title={isPinned ? 'Unpin from saved actions' : 'Pin to saved actions'}
          aria-pressed={isPinned}
          aria-label={isPinned ? 'Unpin message' : 'Pin message'}
          onClick={() => onPin(content)}
        >
          <PinIcon filled={isPinned} />
        </button>
        <button
          type="button"
          className="flow-thread__message-action flow-thread__message-copy"
          title={copied ? 'Copied!' : 'Copy message'}
          aria-label={copied ? 'Copied to clipboard' : 'Copy message to clipboard'}
          onClick={() => void handleCopy()}
        >
          <CopyIcon />
        </button>
      </div>
    </div>
  );
}
